"use client";

/* eslint-disable react/prop-types */
import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown, MapPin } from "lucide-react";
import { collection, getDocs } from "firebase/firestore";
import { db } from "@kkeut/lib/firebase/client";
import { branchText, groupBranches, withoutOpeningNote } from "@kkeut/lib/branch-network";

const T = {
  ko: { all: "전국 지점", select: "지점 선택", loading: "불러오는 중…", error: "지점 목록을 불러오지 못했습니다.", retry: "다시 시도" },
  en: { all: "Locations", select: "Choose a clinic", loading: "Loading…", error: "Couldn't load clinics.", retry: "Retry" },
  ja: { all: "院一覧", select: "院を選択", loading: "読み込み中…", error: "読み込めませんでした。", retry: "再試行" },
  zh: { all: "分院", select: "选择分院", loading: "加载中…", error: "无法加载分院列表。", retry: "重试" },
  th: { all: "สาขา", select: "เลือกสาขา", loading: "กำลังโหลด…", error: "โหลดรายชื่อสาขาไม่สำเร็จ", retry: "ลองอีกครั้ง" },
};

// 목록 스크롤바 — 기본(각진 막대·화살표)이 둥근 모서리를 삐져나와서 4px 둥근 연회색 막대로 (#963).
// 위아래로 2.5만큼 띄워 모서리에 닿지 않게 하고, 투명 테두리로 오른쪽에서 4px 안쪽에 둔다.
// scrollbar-width 를 같이 쓰면 크롬이 아래 설정을 무시하므로 넣지 않는다(파이어폭스는 기본 모양).
const SCROLLBAR =
  "[&::-webkit-scrollbar]:w-3 [&::-webkit-scrollbar-track]:my-2.5 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:[border:4px_solid_transparent] [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:bg-clip-padding";

// 오픈 예정 표시 (#979) — 이름 아래 작게
const Opening = ({ text }) => (
  <span className="mt-0.5 self-start rounded-md bg-[#fff4f5] px-1 text-[11px] font-medium text-[#9a425b]">{text}</span>
);

// 서버 목록이 없을 때만 쓰는 브라우저 조회 — 헤더가 스크롤 전·후로 다시 그려져도 한 번만 읽는다.
let pending = null;
const loadBranchDocs = () =>
  (pending ||= getDocs(collection(db, "branches"))
    .then((snap) => snap.docs.map((d) => ({ ...d.data(), id: d.id })))
    .catch((e) => {
      pending = null;
      throw e;
    }));

/**
 * 상단 지점 이동 드롭다운 (#958). 누르면 버튼이 분홍 탭이 되고 아래로 지역별 세로 목록이 펼쳐진다.
 * PC: 메뉴 맨 앞 "전국 지점" (xl 미만은 아이콘만). 모바일(mobile): 로고 바로 옆, 로고와 같은 높이의 반투명 알약에 지금 지점 이름.
 * 목록은 레이아웃이 서버에서 미리 읽어 넘긴다(initialDocs, #961) — 첫 화면부터 바로 보인다.
 * 서버가 못 읽었으면 알약은 "전국 지점"으로 두고, 목록은 처음 열 때 브라우저에서 읽는다.
 * 지점을 누르면 같은 창에서 그 지점 첫 화면으로.
 */
/**
 * @param {"light"|"dark"} [props.logoTone] - 주면 PC 로고 옆 알약으로 그린다 (지점 이름 표시).
 *   light: 사진 위 흰 글자 헤더 / dark: 흰 바탕 헤더
 */
export function BranchSwitcher({ branchId, locale = "ko", mobile = false, initialDocs = null, logoTone = null }) {
  const t = T[locale] || T.ko;
  const box = useRef(null);
  const trigger = useRef(null);
  const [open, setOpen] = useState(false);
  const [fetched, setFetched] = useState(null);
  const [failed, setFailed] = useState(false);
  const docs = initialDocs ?? fetched;
  const currentName = withoutOpeningNote(branchText(docs?.find((d) => d.id === branchId)?.name, locale)) || t.all;

  const load = () => {
    setFailed(false);
    loadBranchDocs().then(setFetched).catch(() => setFailed(true));
  };

  const toggle = () => {
    if (!open && !docs) load();
    setOpen(!open);
  };

  useEffect(() => {
    if (!open) return;
    const outside = (e) => !box.current?.contains(e.target) && setOpen(false);
    const esc = (e) => {
      if (e.key !== "Escape") return;
      setOpen(false);
      trigger.current?.focus();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", esc);
    };
  }, [open]);

  const groups = docs && groupBranches(docs, branchId, locale);
  const shape = open ? "rounded-b-none bg-[#ffc0cb] text-[#181818]" : "";

  return (
    <div ref={box} className={mobile ? "relative flex min-w-0" : logoTone ? "relative z-50" : "relative -ml-3"}>
      <button
        ref={trigger}
        type="button"
        onClick={toggle}
        aria-expanded={open}
        aria-label={mobile ? `${currentName} · ${t.select}` : t.all}
        className={
          mobile
            ? `flex h-6 min-w-0 items-center gap-0.5 rounded-t-xl px-2.5 text-xs font-bold text-[#181818] ${
                open ? shape : "rounded-b-xl border border-[#ffc0cb]/90 bg-white/30 backdrop-blur-[2px]"
              }`
            : logoTone
              ? `flex h-8 items-center gap-1 whitespace-nowrap rounded-t-2xl border px-3 text-sm font-semibold tracking-normal transition-colors ${
                  open
                    ? "border-[#ffc0cb] bg-[#ffc0cb] text-[#181818]"
                    : `rounded-b-2xl ${
                        logoTone === "light"
                          ? "border-white/60 text-white hover:bg-white/10"
                          : "border-[#ffc0cb] text-[#181818] hover:bg-[#fff4f5]"
                      }`
                }`
              : `flex h-[38px] items-center gap-1 rounded-t-[19px] px-3 ${open ? shape : "rounded-b-[19px]"}`
        }
      >
        {!mobile && <MapPin size={logoTone ? 15 : 18} aria-hidden="true" />}
        <span className={mobile ? "truncate" : logoTone ? "" : "hidden xl:inline"}>{mobile || logoTone ? currentName : t.all}</span>
        <ChevronDown size={mobile ? 13 : 16} aria-hidden="true" className={`shrink-0 ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div
          className={`absolute left-0 top-full z-[60] max-h-[min(460px,calc(100vh-140px))] w-[200px] overflow-y-auto overscroll-contain rounded-b-[14px] rounded-tr-[14px] border border-[#ffc0cb] bg-white py-1.5 text-left text-[15px] font-normal tracking-normal shadow-lg ${SCROLLBAR}`}
        >
          {failed ? (
            <div role="alert" className="px-4 py-6 text-sm text-gray-600">
              <p>{t.error}</p>
              <button type="button" onClick={load} className="mt-2 underline">{t.retry}</button>
            </div>
          ) : !groups ? (
            <p role="status" className="px-4 py-6 text-sm text-gray-500">{t.loading}</p>
          ) : (
            groups.map((g) => (
              <section key={g.key}>
                <p className="px-4 pb-1 pt-3 text-base font-bold text-[#9a425b]">{g.label}</p>
                <ul>
                  {g.items.map((b) => (
                    <li key={b.id}>
                      {b.current ? (
                        <span aria-current="page" className="mx-2 flex min-h-10 items-center justify-between gap-2 break-keep rounded-[10px] bg-[#fff4f5] py-1.5 pl-5 pr-2.5 font-bold text-[#181818]">
                          <span className="flex flex-col">
                            {b.name}
                            {b.opening && <Opening text={b.opening} />}
                          </span>
                          <Check size={15} aria-hidden="true" className="shrink-0 text-[#9a425b]" />
                        </span>
                      ) : b.href ? (
                        <a href={b.href} className="flex min-h-10 flex-col justify-center break-keep py-1.5 pl-7 pr-4 text-gray-600 hover:bg-[#fff4f5] hover:text-[#181818]">
                          {b.name}
                          {b.opening && <Opening text={b.opening} />}
                        </a>
                      ) : (
                        <span aria-disabled="true" className="flex min-h-10 flex-col justify-center break-keep py-1.5 pl-7 pr-4 text-gray-400">
                          {b.name}
                          {b.opening && <Opening text={b.opening} />}
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      )}
    </div>
  );
}

export default BranchSwitcher;
